import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  lifecycle,
  withHandlers
} from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Modal from '@material-ui/core/Modal';
import {
  FormControlLabel,
  ListItem,
  MenuItem,
  Radio,
  RadioGroup,
  Select
} from '@material-ui/core';
import RequiredButton from '../../components/atoms/RequiredButton';
import AnyButton from '../../components/atoms/AnyButton';
import CancelButton from '../../components/atoms/CancelButton';
import ConfirmButton from '../../components/atoms/ConfirmButton';
import FileUpload from '../../components/molecules/FileUpload';
import GochiChildFoodListEditConfirm from './GochiChildFoodListEditConfirm';
import {
  getGochiChildFoodLists,
  editGochiChildFoodList,
  addGochiChildFoodListImage
} from '../../actions/GochiChildFoodLists';
import { resizeImage, uploadFile, sendLogin } from '../../auth/Auth';
import ValidationText from '../../components/atoms/ValidationText';
import { languages, languageType } from '../../constants/Language';

const styles = theme => ({
  root: {
    position: 'absolute',
    width: '85%',
    height: 'auto',
    maxHeight: '80%',
    top: '5%',
    left: '2%',
    backgroundColor: '#fff',
    padding: 28,
    overflow: 'scroll',
    outline: 'none',
    [theme.breakpoints.down('sm')]: {
      width: '95%',
      padding: 16
    }
  },
  pageTitle: {
    margin: '20px 0px 0px',
    paddingLeft: 20,
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
      marginBottom: 10
    }
  },
  mainPaper: {
    position: 'relative',
    margin: '1px 0px 10px',
    padding: '10px 26px 10px 26px',
    [theme.breakpoints.down('sm')]: {
      padding: '10px 5px'
    },
    display: 'flex',
    flexDirection: 'column'
  },
  alignEnd: {
    alignSelf: 'end'
  },
  editTable: {
    width: 'auto',
    [theme.breakpoints.down('sm')]: {
      display: 'block'
    },
    '& tr': {
      [theme.breakpoints.down('sm')]: {
        display: 'block'
      }
    }
  },
  tableCellBorderNone: {
    borderBottom: 'none'
  },
  textField100: {
    padding: '0px 5px 0px 5px',
    width: '100px'
  },
  textField: {
    padding: '0px 5px 0px 5px',
    width: '300px',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  textFieldLong: {
    padding: '0px 5px 0px 5px',
    width: '500px',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  priceCalc: {
    margin: '0px 5px',
    color: '#aaa'
  },
  colLeft: {
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      border: 0,
      paddingBottom: 5
    }
  },
  colRight: {
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      border: 0,
      paddingTop: 0
    }
  },
  hideMobile: {
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  width100: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      display: 'block',
      padding: 0
    }
  }
});

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      name: state.loginInfo.name,
      detail: state.gochiMenus.detail,
      shops: state.shops.shops,
      groups: state.groups.groups
    }),
    dispatch => ({
      actions: bindActionCreators(
        {
          getGochiChildFoodLists,
          editGochiChildFoodList,
          addGochiChildFoodListImage
        },
        dispatch
      )
    })
  ),
  setPropTypes(propTypes),
  withState('openEditProgress', 'updateOpenEditProgress', false),
  withState('loading', 'updateLoading', false),
  withState('isEditSuccess', 'updateIsEditSuccess', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('shopId', 'setShopId', 0),
  withState('shopName', 'setShopName', ''),
  withState('groupId', 'setGroupId', 0),
  withState('groupName', 'setGroupName', ''),
  withState('childFoodId', 'setChildFoodId', 0),
  withState('childFoodName', 'setChildFoodName', ''),
  withState('childFoodNameError', 'setChildFoodNameError', ''),
  withState('childFoodDescription', 'setChildFoodDescription', ''),
  withState('childFoodImage', 'setChildFoodImage', null),
  withState('previewImage', 'setPreviewImage', null),
  withState('isLanguage', 'setIsLanguage', 'ja'),
  withState('translateType', 'setTranslateType', 0),
  withState('multiLangData', 'setMultiLangData', {}),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    }
  }),
  lifecycle({
    componentDidMount() {
      this.props.setShopId(this.props.editInput.shopId);
      this.props.setShopName(this.props.editInput.shopName);
      this.props.setGroupId(this.props.editInput.groupId);
      this.props.setGroupName(this.props.editInput.groupName);
      this.props.setChildFoodId(this.props.editInput.childFoodId);
      this.props.setChildFoodName(this.props.editInput.childFoodName);
      this.props.setChildFoodDescription(
        this.props.editInput.childFoodDescription
      );
      if (this.props.editInput.childFoodImage) {
        this.props.setPreviewImage(
          this.props.editInput.childFoodImage[0].image
        );
      }
      let dataFirst = {};
      const origilData = {
        shopId: null,
        groupId: null,
        childFoodName: null,
        childFoodDescription: null,
        childFoodId: null
      };
      dataFirst = origilData;
      if (this.props.editInput.otherLanguages) {
        const str = JSON.stringify(this.props.editInput.otherLanguages);
        dataFirst.otherLanguages = JSON.parse(str);
      }
      languages.forEach(item => {
        if (item.value === languageType.ORIGIN) return;
        const translatedData = dataFirst.otherLanguages.find(
          el => el.language === item.value
        );
        if (!translatedData) {
          const other = {
            translateType: 0,
            language: item.value
          };
          dataFirst.otherLanguages.push(other);
        }
      });
      console.log('DATA FIRST: ', dataFirst);
      this.props.setMultiLangData(dataFirst);
    }
  })
);
const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    searchKey,
    editInput,
    openEditConfirm,
    updateOpenEdit,
    updateOpenEditConfirm,
    openEditProgress,
    updateOpenEditProgress,
    loading,
    updateLoading,
    isEditSuccess,
    updateIsEditSuccess,
    resultMessage,
    setResultMessage,
    shopId,
    shopName,
    groupId,
    groupName,
    childFoodId,
    childFoodName,
    setChildFoodName,
    childFoodDescription,
    setChildFoodDescription,
    childFoodImage,
    setChildFoodImage,
    previewImage,
    setPreviewImage,
    // updatedAt,
    childFoodNameError,
    setChildFoodNameError,
    isLanguage,
    setIsLanguage,
    multiLangData,
    setMultiLangData,
    translateType,
    setTranslateType
  }) => {
    const errors = {
      childFoodNameError
    };

    const validChildFoodName = () => {
      if (isLanguage !== languageType.ORIGIN && translateType === 0) {
        return 0;
      }
      const value = childFoodName;
      if (value.trim() === '') {
        setChildFoodNameError('必須項目です。');
        return 1;
      }
      setChildFoodNameError('');
      setChildFoodName(value);
      return 0;
    };
    const valid = () => {
      const errorCount = validChildFoodName() + 0;
      if (errorCount === 0) {
        return true;
      }
      return false;
    };

    const getCurrentData = () => {
      const lang = isLanguage;
      let data = multiLangData;
      const origilParam = {
        shopId,
        groupId,
        childFoodName,
        childFoodDescription,
        childFoodId
      };
      const otherParam = {
        translateType,
        language: lang,
        params: {
          childFoodName,
          childFoodDescription
        }
      };
      // origin
      if (lang === languageType.ORIGIN) {
        data = { ...data, ...origilParam };
      } else {
        // other languages
        for (let index = 0; index < data.otherLanguages.length; index += 1) {
          if (data.otherLanguages[index].language === lang) {
            data.otherLanguages[index] = otherParam;
          }
        }
      }
      const str = JSON.stringify(data);
      return JSON.parse(str);
    };

    const cancelAction = () => updateOpenEdit(false);
    const confirmAction = () => {
      if (!valid()) {
        alert('登録情報を正しく入力してください。');
      } else {
        setMultiLangData(getCurrentData());
        updateOpenEditConfirm(true);
      }
    };
    const backAction = () => updateOpenEditConfirm(false);
    const editAction = async () => {
      updateLoading(true);
      updateOpenEditProgress(true);
      const editedChildFoodList = await actions
        .editGochiChildFoodList(childFoodId, multiLangData)
        .then(data => checkSession(data));
      console.log('childFoodImage', childFoodImage);
      if (typeof childFoodImage !== 'undefined' && childFoodImage !== null) {
        let imageData = childFoodImage;
        if (imageData.size > Number(process.env.REACT_APP_RESIZE_BORDER)) {
          const { resizeImageFile } = await resizeImage(imageData);
          imageData = resizeImageFile;
        }
        console.log(imageData);
        const imagePath = await uploadFile(
          'child_food',
          childFoodId,
          imageData,
          'public'
        );
        actions
          .addGochiChildFoodListImage(childFoodId, {
            images: [
              {
                image: imagePath
              }
            ]
          })
          .then(data => checkSession(data));
      }
      if (editedChildFoodList.type === 'EDIT_GOCHI_CHILD_FOOD_SUCCESS') {
        updateIsEditSuccess(true);
        actions
          .getGochiChildFoodLists(searchKey)
          .then(data => checkSession(data));
        setResultMessage('編集が完了しました。');
        updateLoading(false);
      } else {
        updateIsEditSuccess(false);
        setResultMessage('編集に失敗しました。');
        updateLoading(false);
      }
    };
    const progressOkAction = () => {
      if (isEditSuccess) {
        updateOpenEdit(false);
        updateOpenEditConfirm(false);
        updateOpenEditProgress(false);
      } else {
        updateOpenEditProgress(false);
      }
    };

    const setLanguage = v => {
      const lang = isLanguage;
      const data = getCurrentData();
      if (
        (v !== languageType.ORIGIN && lang === languageType.ORIGIN) ||
        (v !== languageType.ORIGIN && lang !== languageType.ORIGIN)
      ) {
        // origin to other or other to other lang
        for (let k = 0; k < multiLangData.otherLanguages.length; k += 1) {
          if (multiLangData.otherLanguages[k].language === v) {
            setChildFoodName(
              multiLangData.otherLanguages[k].params &&
                multiLangData.otherLanguages[k].params.childFoodName
                ? multiLangData.otherLanguages[k].params.childFoodName
                : ''
            );
            setChildFoodDescription(
              multiLangData.otherLanguages[k].params &&
                multiLangData.otherLanguages[k].params.childFoodDescription
                ? multiLangData.otherLanguages[k].params.childFoodDescription
                : ''
            );
            setTranslateType(
              multiLangData.otherLanguages[k].translateType
                ? multiLangData.otherLanguages[k].translateType
                : 0
            );
          }
        }
      } else if (v === languageType.ORIGIN) {
        // other to origin
        setChildFoodName(multiLangData.childFoodName);
        setChildFoodDescription(multiLangData.childFoodDescription);
      }
      setIsLanguage(v);
      setMultiLangData(data);
    };
    return (
      <div className={classes.root}>
        <div className={classes.pageTitle}>
          <Typography variant="h6">メニュー編集</Typography>
        </div>
        <Paper className={classes.mainPaper} elevation={1}>
          <Select
            className={`${classes.textField} ${classes.alignEnd}`}
            value={isLanguage}
            onChange={e => {
              if (isLanguage === e.target.value) {
                return;
              }
              if (
                valid() ||
                (isLanguage !== languageType.ORIGIN && translateType === 0)
              ) {
                setLanguage(e.target.value);
              } else {
                alert('登録情報を正しく入力してください。');
              }
            }}
          >
            {languages.map(lang => (
              <MenuItem key={lang.value} value={lang.value}>
                {lang.label}
              </MenuItem>
            ))}
          </Select>
          {isLanguage !== languageType.ORIGIN && (
            <ListItem className={classes.itemForm}>
              <Typography component="div" className={classes.formInput}>
                <RadioGroup
                  value={translateType}
                  onChange={e => {
                    setTranslateType(parseInt(e.target.value, 10));
                  }}
                >
                  <FormControlLabel
                    value={0}
                    control={<Radio color="primary" />}
                    label="GG翻訳を選択"
                  />
                  <FormControlLabel
                    value={1}
                    control={<Radio color="primary" />}
                    label="自分で文章入力"
                  />
                </RadioGroup>
              </Typography>
            </ListItem>
          )}
          <Table className={classes.editTable}>
            {isLanguage === languageType.ORIGIN && (
              <TableRow>
                <TableCell className={classes.colLeft}>
                  <RequiredButton />
                </TableCell>
                <TableCell className={classes.colRight}>
                  <TextField
                    required
                    label="店舗名"
                    defaultValue={editInput.shopName}
                    className={classes.textField}
                    margin="normal"
                    InputProps={{
                      readOnly: true
                    }}
                    variant="filled"
                  />
                </TableCell>
                <TableCell className={classes.width100} />
              </TableRow>
            )}
            {isLanguage === languageType.ORIGIN && (
              <TableRow>
                <TableCell className={classes.colLeft}>
                  <RequiredButton />
                </TableCell>
                <TableCell className={classes.colRight}>
                  <TextField
                    required
                    label="グループ名"
                    defaultValue={editInput.groupName}
                    className={classes.textField}
                    margin="normal"
                    InputProps={{
                      readOnly: true
                    }}
                    variant="filled"
                  />
                </TableCell>
                <TableCell className={classes.width100} />
              </TableRow>
            )}
            <TableRow>
              <TableCell className={classes.colLeft}>
                <RequiredButton />
              </TableCell>
              <TableCell className={classes.colRight}>
                <TextField
                  required
                  label="メニュー名"
                  defaultValue={editInput.childFoodName}
                  className={classes.textField}
                  margin="normal"
                  onChange={e => {
                    setChildFoodName(e.target.value);
                  }}
                  onBlur={validChildFoodName}
                  value={childFoodName}
                  disabled={
                    isLanguage !== languageType.ORIGIN && translateType === 0
                  }
                  inputProps={{
                    maxLength: isLanguage === languageType.ORIGIN ? 250 : 1000
                  }}
                />
                <ValidationText message={errors.childFoodNameError} />
              </TableCell>
              <TableCell className={classes.width100} />
            </TableRow>
            <TableRow>
              <TableCell className={classes.colLeft}>
                <AnyButton />
              </TableCell>
              <TableCell className={classes.colRight}>
                <TextField
                  label="説明"
                  multiline
                  rows="4"
                  defaultValue={editInput.childFoodDescription}
                  className={classes.textFieldLong}
                  margin="normal"
                  onChange={e => {
                    setChildFoodDescription(e.target.value);
                  }}
                  value={childFoodDescription}
                  inputProps={{
                    maxLength: isLanguage === languageType.ORIGIN ? 1000 : 2500
                  }}
                  disabled={
                    isLanguage !== languageType.ORIGIN && translateType === 0
                  }
                />
              </TableCell>
              <TableCell className={classes.width100} />
            </TableRow>
            {isLanguage === languageType.ORIGIN && (
              <TableRow>
                <TableCell
                  className={[classes.tableCellBorderNone, classes.colLeft]}
                >
                  <AnyButton />
                </TableCell>
                <TableCell
                  className={[classes.tableCellBorderNone, classes.colRight]}
                >
                  <FileUpload
                    label="image"
                    file={childFoodImage}
                    setFile={setChildFoodImage}
                    previewImage={previewImage}
                    setPreviewImage={setPreviewImage}
                  />
                </TableCell>
                <TableCell className={classes.tableCellBorderNone} />
              </TableRow>
            )}
          </Table>
        </Paper>
        <div>
          <CancelButton onClickAction={cancelAction} color="primary" />
          <ConfirmButton onClickAction={confirmAction} />
        </div>
        <Modal open={openEditConfirm}>
          <GochiChildFoodListEditConfirm
            backAction={backAction}
            editAction={editAction}
            editConfirm={multiLangData}
            groupName={groupName}
            shopName={shopName}
            previewImage={previewImage}
            currentLanguage={isLanguage}
            openEditProgress={openEditProgress}
            progressOkAction={progressOkAction}
            loading={loading}
            resultMessage={resultMessage}
          />
        </Modal>
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
